import axios from 'axios';
import PropTypes from 'prop-types';
import React, { Component, createRef } from 'react';
import { injectIntl } from 'react-intl';
import { connect } from "react-redux";
import { autoCompleteURL, placeDetailURL } from '../../../config';
import { debounce } from '../../../helpers/debounce';
import { googleMapLoader } from '../../../helpers/googleMapLoader';
import { decode } from '../../../helpers/queryEncryption';
import messages from '../../../locale/messages';

const styles = {
	popularLocationsDropdown: {
		position: 'absolute',
		top: '100%',
		left: 0,
		right: 0,
		background: 'var(--background-primary)',
		border: '1px solid var(--primary-border)',
		borderTop: 0,
		zIndex: 1,
		borderRadius: '20px',
		boxShadow: '0 2px 4px var(--primary-box-shadow)',
		maxHeight: 300,
		overflowY: 'auto',
	},
	popularLocationsHeader: {
		padding: '10px 12px 6px',
		fontSize: 13,
		fontWeight: 600,
		color: 'var(--muted-foreground)',
		textTransform: 'uppercase',
		letterSpacing: '0.5px',
	},
	popularLocationItem: {
		display: 'flex',
		alignItems: 'center',
		padding: '8px 12px',
		cursor: 'pointer',
		gap: 10,
		transition: 'background 0.15s',
	},
	popularLocationImage: {
		width: 36,
		height: 36,
		borderRadius: 4,
		objectFit: 'cover',
		flexShrink: 0,
	},
	popularLocationName: {
		fontSize: 14,
		color: 'var(--primary-foreground)',
		fontWeight: 500,
	},
};

class PlaceGeoSuggest extends Component {

	static propTypes = {
		label: PropTypes.string,
		className: PropTypes.string,
		containerClass: PropTypes.string,
		containerClassName: PropTypes.string,
		initialValue: PropTypes.any,
		googleMaps: PropTypes.object,
		popularLocations: PropTypes.array,
		includedRegionCodes: PropTypes.array,
		regionCode: PropTypes.string,
		locationBias: PropTypes.object,
	};

	constructor(props) {
		super(props);
		this.state = {
			googleMapsApiLoaded: false,
			value: '',
			suggestion: [],
			showPopularLocations: false,
		};
		this.dropdownRef = createRef();
		// Ventana en la que se ignora el focus programático que sigue a una
		// selección (burbuja al fieldWrapper + el.focus()). Solo cubre ese
		// focus automático; un click/focus explícito posterior sí reabre.
		this.suppressFocusOpenUntil = 0;
	}

	async componentDidMount() {
		const { id, initialValue, googleMapBrowserApiKey } = this.props;
		this.setState({ value: initialValue });
		await googleMapLoader(googleMapBrowserApiKey);
		this.setState({ googleMapsApiLoaded: true });
		const isDocument = typeof document !== undefined;
		if (isDocument) {
			document.addEventListener('mousedown', this.handleClickOutside);
			document.addEventListener('touchstart', this.handleClickOutside);
		}
	}

	componentWillUnmount() {
		const isDocument = typeof document !== undefined;
		if (isDocument) {
			document.addEventListener('mousedown', this.handleClickOutside);
			document.addEventListener('touchstart', this.handleClickOutside);
		}
	}

	handleClickOutside = (event) => {
		const { id } = this.props;
		if (this.dropdownRef?.current && !this.dropdownRef?.current?.contains(event?.target)) {
			this.setState({ suggestion: [], showPopularLocations: false });
			const parentSection = document.getElementById('container' + id);
			const childSection = document.getElementById('suggestItem' + id);
			if (parentSection && childSection) {
				parentSection.removeChild(childSection);
			}
		}
	}

	UNSAFE_componentWillReceiveProps(nextProps) {
		const { initialValue } = nextProps;
		this.setState({ value: initialValue });
	}

	handleInputFocus = () => {
		// Ignora solo el focus programático inmediato a una selección.
		// Un focus explícito del usuario (click posterior en el input) sí reabre.
		if (Date.now() < this.suppressFocusOpenUntil) {
			return;
		}
		this.setState({ showPopularLocations: true, suggestion: [] });
	}

	handleInputChange = (e) => {
		const { onChange, popularLocations } = this.props;
		const value = e?.target?.value;
		this.setState({ value });
		onChange && onChange(value);

		if (value?.length > 0) {
			this.setState({ showPopularLocations: false });
			const filtered = (popularLocations || []).filter(item =>
				item?.locationAddress?.toLowerCase()?.includes(value?.toLowerCase())
			);
			if (filtered.length > 0) {
				this.setState({ suggestion: filtered });
			} else {
				this.setState({ suggestion: [] });
				this.handleAutoComplete(e);
			}
		} else {
			this.setState({ suggestion: [], showPopularLocations: false });
		}
	}

	handlePopularSelect = async (item) => {
		const { onSuggestSelect, onChange } = this.props;
		// Suprime el focus programático que sigue a esta selección (~burbuja +
		// el.focus() del fieldWrapper). Caduca rápido para no bloquear un
		// focus explícito posterior del usuario.
		this.suppressFocusOpenUntil = Date.now() + 500;
		this.setState({ value: item.locationAddress, showPopularLocations: false, suggestion: [] });
		onChange && onChange(item.locationAddress);

		try {
			if (window.google?.maps?.Geocoder) {
				const geocoder = new window.google.maps.Geocoder();
				const response = await new Promise((resolve, reject) => {
					geocoder.geocode({
						address: item.locationAddress, componentRestrictions: {
							country: 'BO'
						}
					}, (results, status) => {
						if (status === 'OK' && results?.length > 0) {
							resolve(results[0]);
						} else {
							reject(new Error('Geocode error: ' + status));
						}
					});
				});

				const addressComponents = response.address_components?.map(comp => ({
					types: comp.types,
					shortText: comp.short_name,
					longText: comp.long_name,
				})) || [];

				const data = {
					addressComponents,
					location: {
						latitude: response.geometry.location.lat(),
						longitude: response.geometry.location.lng()
					},
					viewport: {
						high: {
							latitude: response.geometry.viewport.getNorthEast().lat(),
							longitude: response.geometry.viewport.getNorthEast().lng()
						},
						low: {
							latitude: response.geometry.viewport.getSouthWest().lat(),
							longitude: response.geometry.viewport.getSouthWest().lng()
						}
					}
				};

				onSuggestSelect && onSuggestSelect(data, item.locationAddress);
			} else {
				onSuggestSelect && onSuggestSelect(null, item.locationAddress);
			}
		} catch (error) {
			console.error('Geocode error:', error);
			onSuggestSelect && onSuggestSelect(null, item.locationAddress);
		}
	}

	handleAutoComplete = debounce(async (e) => {
		const { onChange, googleMapBrowserApiKey, includedRegionCodes, regionCode, locationBias } = this.props;
		const value = e?.target?.value;
		onChange && onChange(value);
		if (value?.length > 0) {
			const requestData = {
				input: value
			};
			if (Array.isArray(includedRegionCodes) && includedRegionCodes.length > 0) {
				requestData.includedRegionCodes = includedRegionCodes;
			}
			if (regionCode) {
				requestData.regionCode = regionCode;
			}
			if (locationBias) {
				requestData.locationBias = locationBias;
			}
			axios.post(
				autoCompleteURL,
				requestData,
				{
					headers: {
						'Content-Type': 'application/json',
						'X-Goog-Api-Key': decode(googleMapBrowserApiKey)
					}
				}
			).then(response => {
				if (response?.data?.suggestions?.length > 0) {
					this.setState({
						suggestion: response?.data?.suggestions
					});
				}
			}).catch(error => {
				console.error('Error:', error?.response ? error?.response?.data : error?.message);
			});

		}
	}, 100)

	handleSuggest = async ({ placeId, value }) => {
		const { onSuggestSelect, googleMapBrowserApiKey } = this.props;
		if (placeId) {
			this.suppressFocusOpenUntil = Date.now() + 500;
			try {
				const response = await axios.get(`${placeDetailURL}${placeId}`, {
					headers: {
						'Content-Type': 'application/json',
						'X-Goog-Api-Key': decode(googleMapBrowserApiKey),
						'X-Goog-FieldMask': '*'
					}
				});
				if (response?.data) {
					onSuggestSelect(response?.data, value);
					this.setState({ value, suggestion: [] });
				}
			} catch (error) {
				console.error('Place Details Error:', error?.response?.data || error?.message);
			}
		}
	}

	handleGeoSuggestFailure = (e) => {
		const { id } = this.props;
		const { formatMessage } = this.props.intl;
		const suggestionsContainer = document.getElementById('container' + id);
		suggestionsContainer.style.display = "block";
		let suggestionItem = document.createElement("div");
		suggestionItem.id = 'suggestItem' + id;
		suggestionItem.classList.add('suggestItem')
		suggestionItem.textContent = formatMessage(messages.locationWarning);
		if (e.target.value != "") {
			if (!document.getElementById('suggestItem' + id)) {
				suggestionsContainer.appendChild(suggestionItem);
			}
		} else {
			const parentSection = document.getElementById('container' + id);
			const childSection = document.getElementById('suggestItem' + id);
			if (parentSection && childSection) {
				parentSection.removeChild(childSection);
			}
		}
	}

	renderPopularLocations() {
		const { popularLocations, locationuploadDir } = this.props;
		const { showPopularLocations } = this.state;
		if (!showPopularLocations || !popularLocations?.length) return null;

		const path = locationuploadDir?.replace(".", "") || '';

		return (
			<div style={styles.popularLocationsDropdown} className="popularLocationsDropdown">
				<div style={styles.popularLocationsHeader}>Destinos más buscados</div>
				{popularLocations.map((item, index) => (
					<div
						key={index}
						style={styles.popularLocationItem}
						onMouseEnter={(e) => e.currentTarget.style.background = 'var(--common-hover-bg)'}
						onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
						onClick={(e) => { e.stopPropagation(); this.handlePopularSelect(item); }}
					>
						{item?.image && (
							<img
								src={path + item.image}
								alt={item.locationAddress}
								style={styles.popularLocationImage}
							/>
						)}
						<span style={styles.popularLocationName}>{item.locationAddress}</span>
					</div>
				))}
			</div>
		);
	}

	renderSuggestionList() {
		const { suggestion } = this.state;
		if (!suggestion?.length) return null;

		const isPopularSuggestion = suggestion?.[0]?.locationAddress !== undefined;

		return (
			<ul className='manualGeoSuggestListWrapper'>
				{suggestion.map((item, itemIndex) => {
					if (isPopularSuggestion) {
						return (
							<li
								key={itemIndex}
								className='manualGeoSuggestList'
								onClick={(e) => { e.stopPropagation(); this.handlePopularSelect(item); }}
							>
								{item?.locationAddress}
							</li>
						);
					}
					return (
						<li
							key={itemIndex}
							className='manualGeoSuggestList'
							onClick={(e) => {
								e.stopPropagation(); this.handleSuggest({
									placeId: item?.placePrediction?.placeId,
									value: item?.placePrediction?.text?.text
								});
							}}
						>
							{item?.placePrediction?.text?.text}
						</li>
					);
				})}
			</ul>
		);
	}

	render() {
		const { placeholder, className, id, containerClass } = this.props;
		const { googleMapsApiLoaded, value, suggestion } = this.state;
		return (
			<div className={containerClass} ref={this.dropdownRef}>
				{
					googleMapsApiLoaded ?
						<div className='placeGeoSuggestContainer'>
							<div className='manualGeoSuggestInputContainer'>
								<input
									id={id}
									type='text'
									className={className}
									placeholder={placeholder}
									value={value}
									autoComplete='off'
									onFocus={this.handleInputFocus}
									onChange={this.handleInputChange}
									onKeyDown={this.props.onKeyDown}
								/>

								{this.renderPopularLocations()}
								{this.renderSuggestionList()}
							</div>
						</div> :
						<div className='manualGeoSuggestInputContainer'>
							<input
								type='text'
								className={className}
								placeholder={placeholder}
								/* value={value} */
								autoComplete='off'
							/* onChange={(e) => this.handleGeoSuggestFailure(e)} */
							/>
							<div id={'container' + id} className='safariManualGeoSuggestListWrapper' />
						</div>
				}
			</div>
		)
	}
}

const mapState = (state) => ({
	googleMapBrowserApiKey: state?.encodeConfigSettings?.googleMapBrowserApiKey,
});

export default injectIntl(connect(mapState)(PlaceGeoSuggest));
